import { type PromptFormApi } from '@/app/_common/prompt-form'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { T } from '@tolgee/react'
import React, { useEffect } from 'react'
import { useAuth } from '../_auth/auth-hooks'
import { useDomainFromUrl } from './domain/use-domain-from-url'

export function PromptFormFooter(form: PromptFormApi) {
  const { user } = useAuth()
  const { domainId } = useDomainFromUrl()
  const [selectedCategory, setSelectedCategory] = React.useState<string>('')

  const currentPrompt = form.getState().values.prompt?.trim()

  const templates = React.useMemo(() => {
    if (domainId === `${user?.uid}-default`) {
      return PROMPT_TEMPLATES.default
    }
    return PROMPT_TEMPLATES[domainId] ?? {}
  }, [domainId, user?.uid])

  const categories = React.useMemo(() => {
    return Object.keys(templates)
  }, [templates])

  useEffect(() => {
    if (!selectedCategory && categories.length) {
      setSelectedCategory(categories[0])
    }
  }, [categories])

  return (
    <div className='w-full'>
      <p className='mb-10 mt-3 text-center text-xs font-light text-neutral-500'>
        <T keyName={'chat.promptTextarea.disclaimerV2'} />
      </p>

      <div className={cn('mt-4 flex flex-col gap-8')}>
        <div className='flex gap-4'>
          {categories.map((category) => {
            return (
              <Button
                key={category}
                className={cn('rounded-lg p-2 text-xs font-[450] text-neutral-500', {
                  'bg-mango-100 font-[450] text-neutral-500': category === selectedCategory,
                })}
                onClick={() => setSelectedCategory(category)}
                unstyled
              >
                <p>{category}</p>
              </Button>
            )
          })}
        </div>
        <div className='grid min-h-[200px] grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3'>
          {selectedCategory &&
            templates[selectedCategory]?.map((template) => {
              const isSelected = template.prompt === currentPrompt
              return (
                <PromptTemplateCard
                  key={template.title}
                  form={form}
                  prompt={template.prompt}
                  title={template.title}
                  isSelected={isSelected}
                />
              )
            })}
        </div>
      </div>
    </div>
  )
}

type PromptTemplateData = {
  title: string
  prompt: string
}

type PromptTemplateCardProps = {
  isSelected: boolean
  form: PromptFormApi
} & PromptTemplateData

function PromptTemplateCard({ isSelected, form, prompt, title }: PromptTemplateCardProps) {
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    form.change('prompt', isSelected ? '' : prompt)
    e.currentTarget.blur()

    // Focus the textarea after changing the prompt
    // Use a small delay to ensure the DOM has updated
    setTimeout(() => {
      // Get the localized aria-label text
      const textarea = document.querySelector(`textarea`) as HTMLTextAreaElement

      if (textarea) {
        textarea.focus()
        // Move cursor to end of text
        textarea.setSelectionRange(textarea.value.length, textarea.value.length)
      }
    }, 0)
  }

  const className = cn(
    'flex h-[92px] w-full items-center rounded-lg border border-neutral-50 bg-neutral-25 px-8 py-4 text-sm text-left font-normal text-neutral-500 transition-[outline] ease-in-out',
  )

  return (
    <Button onClick={handleClick} className={className} unstyled>
      <span title={title} className='line-clamp-3'>
        {title}
      </span>
    </Button>
  )
}

const PROMPT_TEMPLATES: Record<string, Record<string, { title: string; prompt: string }[]>> = {
  ['default']: {
    ['Featured']: [
      {
        title: 'Mobile-First UI PRD for E-Commerce',
        prompt:
          "I am building an e-commerce platform in the health and fitness space. Research the principles of UI/UX design for e-commerce platforms, with a focus towards mobile-first apps. Using this research, create a template for a product requirements document for my team to build a MVP UI. Pre-populate this template with details that might be relevant to the app I'm building. Publish this in Confluence and then invite my team to review and edit by sharing the page to #craft-notifications",
      },
      {
        title: 'Reddit Issues Weekly Report',
        prompt:
          'Scan and search r/bugs, r/help, r/TheoryOfReddit, r/softwaregore for problem users have faced with the reddit website over the past week and create a confluence page, once the page is created send the link to #craft-notifications',
      },
      {
        title: 'AI Research Papers Ranked by Relevance',
        prompt:
          'Analyze the latest research papers from DeepMind, and extract key technological breakthroughs and potential applications. Rank them in terms of relevance to AI agents, and create a summary report. Create a Confluence page with this info for the research team to review.',
      },
    ],
    ['Enterprise Automation']: [
      {
        title: 'Strategic Analysis of Health Insurance Investments',
        prompt:
          'Research significant investments, network expansions, or acquisitions, indicating strategic direction for the top 5 largest health insurance companies by market capitalization. Write a report detailing the findings, highlighting key financial metrics and strategic investments. Do a thorough analysis of how recent strategic moves at each of these companies relate to trends in the broader health insurance industry.',
      },
      {
        title: 'Emerging Wellness Brands Market Report',
        prompt:
          "I am building a company focused on creating a curated marketplace for retailers focused on the health and wellness space. Can you search Product Hunt and relevant Reddit threads (r/supplements, r/Fitness, r/HealthyFood, and r/DTCentrepreneurs) for up-and-coming brands that I should reach out to? Explain why I should reach out to these brands and evaluate the value propositions of each. Using these findings, create a comprehensive market report that I can use to evaluate future partnerships. Include the links to each of these companies' websites.",
      },
      {
        title: 'MRD for Peer-Based Mental Wellness Platform',
        prompt:
          'Create a comprehensive market requirements document (MRD) for a mental wellness platform connecting users to trained peers with similar experiences. The document should include a detailed discussion of the total addressable market, key target personas and their needs, and potential success metrics. Add a section in the MRD titled "Competitor Analysis" and populate it with research of most notable features of similar platforms. Discuss how can we differentiate ourselves from these competitors, and write a report with the findings. Publish this page on Confluence.',
      },
    ],
    ['Web Automation']: [
      {
        title: 'Run vendor compliance check for Acme Logistics UK',
        prompt: `I’m onboarding a new vendor named 'Acme Logistics Ltd' from the UK. Can you run a compliance check across relevant sources? I need to verify their registration status on UK Company House, check against denied party lists on https://www.trade.gov/consolidated-screening-list, and confirm the accuracy of their company ID and business address. Provide links for cross reference where possible. Just use the Web Agent. Here are the details Company ID: 06995453 Address: 12-14 High Street, Poole, Dorset, BH15 1BP. I don't have any other details`,
      },
      {
        title: 'Fill supplier registration form for Avro Supplies',
        prompt: `Hey, can you fill out this supplier registration form for me? Here's the link: https://forms.office.com/r/nTzKbktvxS. Use these details: Avro Supplies LLC, ID 123456789, Country UAE, Tax ID VAT-12345678, Registration Date July 25, 2025, and Email contact@acmesupplies.com. Just use the web agent to get it done.`,
      },
      {
        title: 'Broken Link Report for Website Testbed',
        prompt:
          'Test https://website-testbed.replit.app/ for broken links and create a confluence page with the summary of the results. Include links to the pages that you want me to manually follow-up on in the report.',
      },
      {
        title: 'E-Commerce Homepage Analysis by Rubric',
        prompt:
          "Analyze the websites of e-commerce platforms Shopify, BigCommerce, Wix Stores, and Squarespace Commerce. Evaluate the effectiveness of the homepage in highlighting each platform's unique advantage and create a detailed rubric that evaluates the effectiveness of each website and alignment to e-commerce website design best practices. Write a detailed report that explains each part of the rubric and can be shared with the marketing team for an upcoming website refresh. Publish this page on Confluence.",
      },
      {
        title: 'Product Hunt Launches and Value Propositions',
        prompt:
          'Show me the top 5 products launching today as given on product hunt. For each product, go to their Web site, and extract the main value proposition. Save in Confluence.',
      },
    ],
  },
}
